import React, { useState, useEffect, useRef } from 'react';
import Modal from 'react-bootstrap/Modal';
import styles from './Confirm.module.css';

interface ConfirmProps {
  show: boolean;
  title?: string;
  message?: string;
  onClose: (result: 'Yes' | 'No') => void;
  confirmText?: string;
  cancelText?: string;
}

const Confirm: React.FC<ConfirmProps> = ({
  show,
  title = '확인',
  message = '정말로 진행하시겠습니까?',
  onClose,
  confirmText = '확인',
  cancelText = '취소'
}) => {
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [inputCode, setInputCode] = useState<string[]>(['', '', '', '']);
  const [isConfirmEnabled, setIsConfirmEnabled] = useState<boolean>(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // 4자리 알파뉴메릭 랜덤 코드 생성
  const generateVerificationCode = (): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 4; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  // 컴포넌트가 표시될 때마다 새로운 검증 코드 생성
  useEffect(() => {
    if (show) {
      setVerificationCode(generateVerificationCode());
      setInputCode(['', '', '', '']);
      setIsConfirmEnabled(false);
    }
  }, [show]);

  // 입력 코드가 검증 코드와 일치하는지 확인
  useEffect(() => {
    const inputString = inputCode.join('');
    setIsConfirmEnabled(inputString === verificationCode);
  }, [inputCode, verificationCode]);

  // 입력 필드 변경 핸들러
  const handleInputChange = (index: number, value: string) => {
    const newInputCode = [...inputCode];
    newInputCode[index] = value.toUpperCase();
    setInputCode(newInputCode);

    // 다음 입력 필드로 자동 이동
    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // 백스페이스 처리
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !inputCode[index] && index > 0) {
      const newInputCode = [...inputCode];
      newInputCode[index - 1] = '';
      setInputCode(newInputCode);
      inputRefs.current[index - 1]?.focus();
    }
  };

  // 확인 버튼 클릭
  const handleConfirm = () => {
    if (isConfirmEnabled) {
      onClose('Yes');
    }
  };

  // 취소 버튼 클릭
  const handleCancel = () => {
    onClose('No');
  };

  return (
    <Modal 
      show={show} 
      animation={false} 
      centered={false}
      className={styles.confirmModal}
      backdrop="static"
      keyboard={false}
      size="sm"
      dialogClassName={styles.customDialog}
      contentClassName={styles.customContent}
    >
      <Modal.Body className={styles.modalBody}>
        <div className={styles.header}>
          <h3 className={styles.title}>{title}</h3>
        </div>
        
        <div className={styles.content}>
          <p className={styles.message}>{message}</p>
          
          <div className={styles.verificationSection}>
            <p className={styles.verificationLabel}>
              아래 4자리 코드를 입력해주세요:
            </p>
            <div className={styles.codeDisplay}>
              <span className={styles.codeText}>{verificationCode}</span>
            </div>
            
            <div className={styles.inputContainer}>
              {inputCode.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => { inputRefs.current[index] = el; }}
                  type="text"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleInputChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className={`${styles.codeInput} ${
                    digit ? styles.codeInputFilled : ''
                  }`}
                  placeholder=""
                />
              ))}
            </div>
            
            <div className={styles.verificationStatus}>
              {isConfirmEnabled ? (
                <span className={styles.statusSuccess}>✓ 코드가 일치합니다</span>
              ) : (
                <span className={styles.statusPending}>코드를 입력해주세요</span>
              )}
            </div>
          </div>
        </div>
        
        <div className={styles.footer}>
          <button
            onClick={handleCancel}
            className={`${styles.button} ${styles.buttonCancel}`}
          >
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isConfirmEnabled}
            className={`${styles.button} ${styles.buttonConfirm} ${
              isConfirmEnabled ? styles.buttonConfirmEnabled : styles.buttonConfirmDisabled
            }`}
          >
            {confirmText}
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default Confirm;
